"use client";

import { useEffect } from "react";
import { reportClientError } from "@/lib/report-client-error";

/**
 * Last-resort boundary: an error in the ROOT layout itself. Next.js replaces
 * the entire document with this component, so it must render its own
 * <html>/<body> and can rely on no app CSS being present — styles are inline.
 * Reports through the same pipeline as everything else, then offers a reload
 * (there is no partial tree left to `reset` into).
 *
 * i18n: deliberately NOT localized. This boundary renders OUTSIDE the root
 * layout, so no LinguiClientProvider exists here — useLingui/Trans would
 * throw. The copy stays English by design.
 */
export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string };
}) {
  useEffect(() => {
    reportClientError("react-global", error.message, error.stack, error.digest);
  }, [error]);

  return (
    <html lang="en">
      <body style={{ margin: 0, fontFamily: "system-ui, sans-serif", background: "#faf6ef", color: "#1a1a1a" }}>
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem" }}>
          <div style={{ maxWidth: "24rem", textAlign: "center" }}>
            <h1 style={{ fontSize: "1.25rem", marginBottom: "0.5rem" }}>Something went wrong</h1>
            <p style={{ fontSize: "0.875rem", opacity: 0.7, lineHeight: 1.5, marginBottom: "1.5rem" }}>
              The app hit an unexpected error. It has been reported — reloading usually fixes it.
            </p>
            <button
              type="button"
              onClick={() => { window.location.reload(); }}
              style={{ padding: "0.5rem 1rem", borderRadius: "0.5rem", border: "none", background: "#e8552e", color: "#fff", fontSize: "0.875rem", fontWeight: 500, cursor: "pointer" }}
            >
              Reload
            </button>
          </div>
        </div>
      </body>
    </html>
  );
}
